import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import { SubjectStatus } from '../../../types';
import type { TranscriptPrint } from '../../../types/examination';
import {
  Page,
  pdfColors, pdfSpacing, fmtMarks,
  type SchoolBranding,
} from '../../../utils/pdf';

type Exam = TranscriptPrint['exams'][number];

interface Props {
  /** One page set per transcript, in print order. */
  transcripts: TranscriptPrint[];
  branding: SchoolBranding;
  generatedAt: string;
}

const ACCENT = '#3b82f6';
const RULE = '#e9ecef';
const MUTED = '#6b7280';
const ABSENT = '#ef4444';
const TILES_PER_ROW = 7;
const NUM = { max: 28, obt: 34, pct: 38, grade: 32 } as const;

/** How each result reads in its exam's header — a small tinted chip; `room` is the width it takes, kept clear on both sides so the name stays centred. */
const OUTCOME: Record<'PASS' | 'FAIL' | 'PENDING', { label: string; bg: string; fg: string; room: number }> = {
  PASS: { label: 'Pass', bg: '#dcfce7', fg: '#15803d', room: 32 },
  FAIL: { label: 'Fail', bg: '#fee2e2', fg: '#b91c1c', room: 30 },
  PENDING: { label: 'In progress', bg: '#f1f5f9', fg: '#475569', room: 50 },
};

const pct = (n: number | null) => (n == null ? '—' : `${n.toFixed(1)}%`);

const styles = StyleSheet.create({
  // ── Overall performance summary ─────────────────────────────────────────
  summary: {
    backgroundColor: pdfColors.surfaceSoft, borderWidth: 0.6, borderColor: pdfColors.divider, borderStyle: 'solid',
    borderRadius: 4, paddingVertical: 9, paddingHorizontal: 11, marginBottom: pdfSpacing.md,
  },
  summaryHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  summaryRule: { paddingBottom: 6, marginBottom: 7, borderBottomWidth: 0.6, borderBottomColor: pdfColors.divider, borderBottomStyle: 'solid' },
  summaryTitle: { fontSize: 8, fontWeight: 600, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.3 },
  avgPill: { backgroundColor: ACCENT, borderRadius: 3, paddingVertical: 2.5, paddingHorizontal: 8 },
  avgText: { color: '#ffffff', fontSize: 7.5, fontWeight: 600 },
  tiles: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -3 },
  tileSlot: { paddingHorizontal: 3, paddingVertical: 3 },
  // Every tile in a row as tall as the tallest, so a long subject name doesn't leave the row ragged.
  tile: {
    flexGrow: 1, backgroundColor: '#ffffff', borderWidth: 0.6, borderColor: pdfColors.divider, borderStyle: 'solid', borderRadius: 3,
    paddingVertical: 5, paddingHorizontal: 4, alignItems: 'center', justifyContent: 'center',
  },
  tileName: { fontSize: 6.3, color: pdfColors.textMuted, fontWeight: 500, textTransform: 'uppercase', textAlign: 'center' },
  tileValue: { fontSize: 9.5, fontWeight: 600, marginTop: 2 },

  // ── Exam tables, two to a row ───────────────────────────────────────────
  pair: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  card: { width: '49%', borderWidth: 0.6, borderColor: RULE, borderStyle: 'solid' },
  bar: { backgroundColor: ACCENT, flexDirection: 'row', alignItems: 'center', paddingVertical: 5, minHeight: 20 },
  barText: { flex: 1, color: '#ffffff', fontSize: 8.5, fontWeight: 600, textTransform: 'uppercase', textAlign: 'center', letterSpacing: 0.2 },
  outcome: { alignItems: 'flex-end', paddingRight: 5 },
  chip: { borderRadius: 6, paddingVertical: 1, paddingHorizontal: 4.5 },
  chipText: { fontSize: 5.8, fontWeight: 600 },
  row: { flexDirection: 'row', alignItems: 'center' },
  thead: { backgroundColor: '#f8f9fa', borderBottomWidth: 0.6, borderBottomColor: RULE, borderBottomStyle: 'solid' },
  th: { fontSize: 6.8, fontWeight: 700, color: pdfColors.textPrimary, paddingVertical: 3, paddingHorizontal: 3, textAlign: 'center' },
  td: { fontSize: 6.8, color: pdfColors.textPrimary, paddingVertical: 2.6, paddingHorizontal: 3, textAlign: 'center' },
  cellRule: { borderRightWidth: 0.6, borderRightColor: RULE, borderRightStyle: 'solid' },
  subjectCell: { flex: 1, paddingVertical: 2.6, paddingHorizontal: 3 },
  total: { backgroundColor: '#e8f4f8', borderTopWidth: 1.4, borderTopColor: ACCENT, borderTopStyle: 'solid' },
  bold: { fontWeight: 700 },
  semi: { fontWeight: 600 },

  empty: {
    marginTop: pdfSpacing.md, paddingVertical: 30, textAlign: 'center', fontSize: 9, color: pdfColors.textMuted, fontStyle: 'italic',
    borderWidth: 0.6, borderColor: pdfColors.dividerStrong, borderStyle: 'dashed', borderRadius: 4,
  },
});

/** The numeric cells after a subject, in the server transcript's order. */
const Numbers: React.FC<{ cells: React.ReactNode[]; bold?: boolean }> = ({ cells, bold }) => (
  <>
    {[NUM.max, NUM.obt, NUM.pct, NUM.grade].map((w, i) => (
      <View key={i} style={i < 3 ? [{ width: w }, styles.cellRule] : { width: w }}>
        <Text style={bold ? [styles.td, styles.semi] : styles.td}>{cells[i]}</Text>
      </View>
    ))}
  </>
);

const ExamCard: React.FC<{ exam: Exam }> = ({ exam }) => {
  const outcome = exam.outcome ? OUTCOME[exam.outcome] : null;
  return (
    <View style={styles.card}>
      <View style={styles.bar}>
        <View style={{ width: outcome?.room ?? 8 }} />
        <Text style={styles.barText}>{exam.name}</Text>
        <View style={[styles.outcome, { width: outcome?.room ?? 8 }]}>
          {outcome && (
            <View style={[styles.chip, { backgroundColor: outcome.bg }]}>
              <Text style={[styles.chipText, { color: outcome.fg }]}>{outcome.label}</Text>
            </View>
          )}
        </View>
      </View>
      <View style={[styles.row, styles.thead]}>
        <View style={[{ flex: 1 }, styles.cellRule]}><Text style={[styles.th, { textAlign: 'left' }]}>Subject</Text></View>
        {(['Max', 'Obt', '%', 'Grade'] as const).map((h, i) => (
          <View key={h} style={i < 3 ? [{ width: Object.values(NUM)[i] }, styles.cellRule] : { width: NUM.grade }}>
            <Text style={styles.th}>{h}</Text>
          </View>
        ))}
      </View>
      {exam.rows.map(r => {
        const absent = r.status === SubjectStatus.ABSENT;
        return (
          <View key={r.subject} style={styles.row} wrap={false}>
            <View style={[styles.subjectCell, styles.cellRule]}>
              <Text style={{ fontSize: 6.8, color: pdfColors.textPrimary }}>{r.subject}</Text>
            </View>
            <Numbers cells={[
              fmtMarks(r.max),
              absent ? <Text style={{ color: ABSENT, fontWeight: 500 }}>Absent</Text> : r.obtained == null ? '—' : fmtMarks(r.obtained),
              pct(r.percentage),
              <Text style={{ fontWeight: 600, color: r.color ?? (absent ? ABSENT : MUTED) }}>{r.grade ?? '—'}</Text>,
            ]} />
          </View>
        );
      })}
      <View style={[styles.row, styles.total]} wrap={false}>
        <View style={[styles.subjectCell, styles.cellRule]}><Text style={[{ fontSize: 6.8 }, styles.bold]}>TOTAL</Text></View>
        <Numbers
          bold
          cells={[
            // Nothing counted yet (every paper pending) — no total to show.
            exam.total.max ? fmtMarks(exam.total.max) : '—',
            exam.total.max ? fmtMarks(exam.total.obtained) : '—',
            pct(exam.total.percentage),
            <Text style={{ color: exam.total.color ?? MUTED }}>{exam.total.grade ?? '—'}</Text>,
          ]}
        />
      </View>
    </View>
  );
};

const pairs = <T,>(items: T[]): T[][] =>
  Array.from({ length: Math.ceil(items.length / 2) }, (_, i) => items.slice(i * 2, i * 2 + 2));

/** One student's transcript: letterhead, overall summary, then every exam two to a row. */
const TranscriptPages: React.FC<{ t: TranscriptPrint; branding: SchoolBranding; generatedAt: string }> = ({ t, branding, generatedAt }) => {
  const perRow = Math.min(Math.max(t.subjects.length, 1), TILES_PER_ROW);
  // A hair under the exact share, so rounding never pushes the last tile of a row onto the next.
  const tileWidth = `${Math.floor((100 / perRow) * 100) / 100 - 0.01}%`;
  return (
    <Page
      branding={branding}
      meta={{
        title: `Academic Transcript — ${t.student.name}`,
        subtitle: [t.className && `Class ${t.className}`, t.session].filter(Boolean).join('  ·  '),
        generatedAt,
      }}
      official={{ details: [
        { label: 'Roll Number', value: t.student.rollNumber == null ? '—' : String(t.student.rollNumber) },
        { label: 'Class', value: t.className },
        ...(t.session ? [{ label: 'Session', value: t.session }] : []),
      ] }}
    >
      <View style={styles.summary} wrap={false}>
        <View style={t.subjects.length > 0 ? [styles.summaryHead, styles.summaryRule] : styles.summaryHead}>
          <Text style={styles.summaryTitle}>Overall Performance Summary</Text>
          <View style={styles.avgPill}>
            <Text style={styles.avgText}>Avg Percentage: {t.overall ? `${t.overall.percentage.toFixed(1)}% (${t.overall.grade})` : '—'}</Text>
          </View>
        </View>
        {t.subjects.length > 0 && (
          <View style={styles.tiles}>
            {t.subjects.map(s => (
              <View key={s.name} style={[styles.tileSlot, { width: tileWidth }]}>
                <View style={styles.tile}>
                  <Text style={styles.tileName}>{s.name}</Text>
                  <Text style={[styles.tileValue, { color: s.color }]}>{s.average.toFixed(1)}%</Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>

      {t.exams.length === 0
        ? <Text style={styles.empty}>No exam results recorded for this session yet.</Text>
        : pairs(t.exams).map((pair, i) => (
          <View key={i} style={styles.pair} wrap={false}>
            <ExamCard exam={pair[0]} />
            {pair[1] ? <ExamCard exam={pair[1]} /> : <View style={{ width: '49%' }} />}
          </View>
        ))}
    </Page>
  );
};

/**
 * Academic transcripts — one or many in a single PDF, each starting on its
 * own page: the school's letterhead, the student's overall average and each
 * subject's, then every exam's marks, total and grade (and, where the school
 * has pass rules, whether it was passed).
 */
const TranscriptDocument: React.FC<Props> = ({ transcripts, branding, generatedAt }) => (
  <Document
    title={transcripts.length === 1 ? `Academic Transcript — ${transcripts[0].student.name}` : `Academic Transcripts · ${transcripts.length}`}
    author={branding.name}
  >
    {transcripts.map((t, i) => <TranscriptPages key={i} t={t} branding={branding} generatedAt={generatedAt} />)}
  </Document>
);

export default TranscriptDocument;
